/**
 * Upgrade Modal Component
 *
 * Shows upgrade prompts when users attempt to access premium features
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'
import { getAccountContext, fetchAndCachePlanLimits, getPlanLimits } from '../store/api'
import { getFeatureDisplayName, getTierDisplayName } from './feature-gate'
import { checkAndShowUserAgreement } from './user-agreement'

export function showUpgradePrompt(featureName: string, tierName: 'standard' | 'enterprise') {
  const backdrop = document.createElement('div')
  backdrop.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center'

  const featureDisplay = getFeatureDisplayName(featureName)
  const tierDisplay = getTierDisplayName(tierName)

  const modal = document.createElement('div')
  modal.className = 'relative p-8 border w-full max-w-md shadow-lg rounded-md bg-white'

  modal.innerHTML = `
    <div class="text-center">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
            <svg class="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
        </div>
        <h3 class="text-xl leading-6 font-medium text-gray-900">Upgrade to ${tierDisplay}</h3>
        <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-500">
                The <strong>${featureDisplay}</strong> feature is available on the <strong>${tierDisplay}</strong> plan.
            </p>
            <p class="text-sm text-gray-500 mt-2">
                Upgrade your account to unlock this and other premium features.
            </p>
        </div>
        <div class="items-center px-4 py-3">
            <button id="confirm-upgrade-btn" class="px-4 py-2 bg-indigo-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300">
                Enter Activation Code
            </button>
            <button id="cancel-upgrade-btn" class="mt-3 px-4 py-2 bg-white text-gray-700 text-base font-medium rounded-md w-full shadow-sm border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-300">
                Cancel
            </button>
        </div>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const closeBtn = modal.querySelector('#cancel-upgrade-btn')
  const upgradeBtn = modal.querySelector('#confirm-upgrade-btn')

  const closeModal = () => {
    backdrop.remove()
  }

  closeBtn?.addEventListener('click', closeModal)

  upgradeBtn?.addEventListener('click', () => {
    closeModal()
    showUpgradeFlow(tierName)
  })

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      closeModal()
    }
  })
}

/**
 * Get tier features as HTML list items
 */
function getTierFeatures(tier: PlanType): string {
  const features: Record<PlanType, string[]> = {
    free: [
      '✓ Dashboard access',
      '✓ Data upload',
      '✓ Basic analysis',
      '✓ 1 branch',
    ],
    standard: [
      '✓ Everything in Free',
      '✓ Advanced analysis',
      '✓ AI insights',
      '✓ Member invitations',
      '✓ 1 branch',
    ],
    enterprise: [
      '✓ Everything in Standard',
      '✓ Branch comparison',
      '✓ PDF exports',
      '✓ API access',
      '✓ Unlimited branches',
    ],
  }

  return (
    features[tier]?.map((f) => `<li class="flex items-center">${f}</li>`)
      .join('') || ''
  )
}

/**
 * Show upgrade flow (activation code entry)
 */
export async function showUpgradeFlow(tierName: 'standard' | 'enterprise') {
  // --- [NEW] Check for User Agreement FIRST ---
  const agreed = await checkAndShowUserAgreement()
  if (!agreed) return // User cancelled or error
  // --- [END NEW] ---

  const backdrop = document.createElement('div')
  backdrop.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center'

  const modal = document.createElement('div')
  modal.className = 'relative p-5 border w-full max-w-md shadow-lg rounded-md bg-white'

  const context = getAccountContext()
  const isDowngradeFromEnterprise = context.planType === 'enterprise' && tierName === 'standard'

  modal.innerHTML = `
    <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-gray-900">Activate ${getTierDisplayName(tierName)}</h3>
        <button id="modal-close-x" class="text-gray-400 hover:text-gray-500">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    </div>
    
    <div class="mb-6">
        <p class="text-sm text-gray-600 mb-4">
            ${isDowngradeFromEnterprise
              ? 'Enter a Standard plan code to switch plans.'
              : `Enter your activation code to upgrade to ${getTierDisplayName(tierName)}.`}
        </p>
        
        <div class="space-y-4">
            <div>
                <label for="activation-code" class="block text-sm font-medium text-gray-700">Activation Code</label>
                <input type="text" id="activation-code" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g. ABCD-1234-EFGH-5678">
            </div>
        </div>
        
        <div id="upgrade-error" class="mt-2 text-sm text-red-600 hidden"></div>
        <div id="upgrade-success" class="mt-2 text-sm text-green-600 hidden"></div>
    </div>

    <div class="flex justify-end gap-3">
        <button id="upgrade-cancel" class="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none">
            Cancel
        </button>
        <button id="upgrade-apply" class="px-4 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none">
            Activate
        </button>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const closeBtn = modal.querySelector('#modal-close-x')
  const applyBtn = modal.querySelector('#upgrade-apply')
  const cancelBtn = modal.querySelector('#upgrade-cancel')
  const codeInput = modal.querySelector('#activation-code') as HTMLInputElement
  const errorDiv = modal.querySelector('#upgrade-error') as HTMLDivElement
  const successDiv = modal.querySelector('#upgrade-success') as HTMLDivElement

  const closeModal = () => {
    backdrop.remove()
  }

  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)

  applyBtn?.addEventListener('click', async () => {
    const activationCode = codeInput.value.trim()
    if (!activationCode) {
      errorDiv.textContent = 'Please enter an activation code.'
      errorDiv.classList.remove('hidden')
      return
    }

    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')

    // Disable UI
    if (applyBtn) (applyBtn as HTMLButtonElement).disabled = true
    if (applyBtn) applyBtn.textContent = 'Verifying...'
    if (codeInput) codeInput.disabled = true

    try {
      const upgradeTierCallable = httpsCallable(functions, 'upgradeTier')
      const result = await upgradeTierCallable({ activationCode, targetPlan: tierName })

      const data = result.data as any
      if (data.success) {
        successDiv.textContent = 'Activation successful! Refreshing your plan...'
        successDiv.classList.remove('hidden')

        // Refresh local state
        const oldContext = getAccountContext()
        const oldTier = oldContext.planType

        await fetchAndCachePlanLimits()

        const newContext = getAccountContext()
        const newTier = newContext.planType

        setTimeout(() => {
          closeModal()

          // Simple alert for feedback
          alert(`Successfully activated ${getTierDisplayName(newTier)} plan!`)

          // Force reload to ensure all UI components update
          window.location.reload()
        }, 1500)
      }
    } catch (error: any) {
      console.error('Upgrade failed:', error)
      errorDiv.textContent = error.message || 'Invalid activation code or server error.'
      errorDiv.classList.remove('hidden')

      // Re-enable UI
      if (applyBtn) (applyBtn as HTMLButtonElement).disabled = false
      if (applyBtn) applyBtn.textContent = 'Activate'
      if (codeInput) codeInput.disabled = false
    }
  })
}
